/**
 * Holds a plan that recreates a figure from the user's material to what that figure says. The
 * figures a plan declares are the agent's own reading of the source, and its document text gives a
 * chart's labels and values as an unpaired run of words, so a misread passes every check that
 * compares the drawing to the plan. A vision model that never saw the build compares the two.
 *
 * Only the verdict comes back, as text: the image never enters the agent's own context.
 */
import { mediaImageUrl } from 'js/src/agent/registries/mediaImageUrl';

import type { Conversation } from 'js/src/agent/Conversation';
import type { ScenePlan } from 'js/src/data/html-design';
import { ErrorReporter } from 'js/src/libs/error-reporter';

export const SOURCE_CHECK_TASK = 'html_design_source_check';

const SYSTEM_PROMPT = `You check a plan for a graphic against the figure it recreates.

The image is a page, slide or picture the user gave. The plan names which figure on it the graphic
recreates and lists the elements, figures and words the graphic will show. The page may hold several
figures: judge only the one the plan describes. The figure may be a chart, a diagram, a timeline, a
table or a layout.

Judge two things: the figure's data, and its form.

The data is its values and the category, series and year names those values are attached to.
Headlines, captions, callouts and every other line of copy are the video's own writing and are
never reported, whether or not the figure carries them.

The form is what kind of marks the figure is built from (rings, bars, dots, boxes, arrows, cells,
icons), how many, and how they relate (in a row, nested, branching, joined, around a centre). The
graphic is restyled on purpose and fitted to a wide frame, so colour, type, size, spacing and
animation never count, and neither does re-arranging the same marks to fit, such as a column of
steps laid out as a row.

Report nothing you cannot read off the image.

- "wrong": a value that differs from the one the figure shows, a value attached to the wrong
  category, series or year, or a category or series the figure does not have.
- "missing": a category or series the figure shows that the plan leaves out.
- "form": the plan's elements are a different kind of mark from the figure's, or relate to each
  other differently — a ring drawn as a bar, a grid of dots drawn as a bar, a branching chart drawn
  as a list.

Answer with JSON only, each entry one sentence naming what the figure shows:
{"wrong": [], "missing": [], "form": []}`;

function planBrief(plan: ScenePlan): string {
    const series = (plan.data ?? []).map(({ label, values }) => `- ${label}: ${values.join(', ')}`);
    return [
        `The figure the graphic recreates: ${plan.reproduces}`,
        '',
        'Elements the graphic draws:',
        ...(plan.graphics.length ? plan.graphics.map((graphic) => `- ${graphic}`) : ['- none']),
        `Laid out as: ${plan.layout || 'unspecified'}`,
        '',
        'Figures the graphic plots, in plotting order:',
        ...(series.length ? series : ['- none declared']),
        '',
        `Words the graphic shows: ${plan.text.length ? JSON.stringify(plan.text) : 'none'}`,
    ].join('\n');
}

export interface SourceFindings {
    /** Figures the plan gets wrong against the source. */
    wrong: string[];
    /** Parts of the source the plan leaves out. */
    missing: string[];
    /** Where the plan draws the source as a different kind of graphic. */
    form: string[];
}

function lines(value: unknown): string[] {
    return Array.isArray(value)
        ? value.filter((line): line is string => typeof line === 'string' && !!line.trim())
        : [];
}

function findingsIn(content: string): SourceFindings | null {
    const match = content.match(/\{[\s\S]*\}/);
    if (!match) {
        return null;
    }
    try {
        const parsed = JSON.parse(match[0]) as {
            wrong?: unknown;
            missing?: unknown;
            form?: unknown;
        };
        return {
            wrong: lines(parsed.wrong),
            missing: lines(parsed.missing),
            form: lines(parsed.form),
        };
    } catch {
        return null;
    }
}

/**
 * Where the plan departs from its source. Null when the check could not be made — the
 * source is not an image, no conversation to run it in, or the model failed — so it never stands
 * between a plan and approval.
 *
 * `startCheck` opens the conversation the check runs in: a child of the plan's own tool call, so the
 * exchange is kept with that call and what it costs is counted with the run.
 */
export async function checkAgainstSource(
    plan: ScenePlan,
    startCheck: (systemMessage: string) => Conversation | null,
    parentActionGroup?: string,
): Promise<SourceFindings | null> {
    if (!plan.reproduces || !plan.reproduces_media_id) {
        return null;
    }
    const source = mediaImageUrl(plan.reproduces_media_id);
    if ('error' in source) {
        return null;
    }
    const check = startCheck(SYSTEM_PROMPT);
    if (!check) {
        return null;
    }
    check.addUserMessage([
        { type: 'image_url', image_url: { url: source.url } },
        { type: 'text', text: planBrief(plan) },
    ]);
    try {
        const result = await check.executeWithLLM({ parentActionGroup });
        return result.success && result.message ? findingsIn(result.message) : null;
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        const expected =
            (error as { name?: string })?.name === 'AbortError' ||
            /Rate limit exceeded|Daily usage limit reached|Request aborted/.test(message);
        if (!expected) {
            ErrorReporter.capture(error instanceof Error ? error : new Error(message), {
                extra: { context: SOURCE_CHECK_TASK },
            });
        }
        return null;
    }
}
